import React from "react";

import { Editor } from "~/components/code/editor";
import { FormFieldErrorMessage } from "~/components/form/error-message";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { IconDelete } from "~/components/icon";
import { RadioButtonGroup } from "~/components/radio-button-group";
import { RadioButtonGroupItem } from "~/components/radio-button-group/item";
import { Impulse } from "~/tools/impulse";
import { usePropagateFocusWhenInvalid } from "~/tools/impulse-form/use-propagate-focus-when-invalid";
import { useTemplateVariables } from "~/tools/template-variables/use-template-variables";

import { Widget, WidgetBody, WidgetHeader } from "../../_widget";

import type { FormState } from "./form-state";

export interface SetupDashboardWidgetLinkProps {
	index: number;
	name: string;
	link: FormState;
	onDelete: VoidFunction;
}

export const SetupDashboardWidgetLink: React.FC<SetupDashboardWidgetLinkProps> = ({ index, link, name, onDelete }) => {
	const variables = useTemplateVariables();

	const [expanded, setExpanded] = React.useState(true);
	// TODO do not convert to Impulse https://stackstate.atlassian.net/browse/OBSFE-171
	// TODO it should use the useState for proper flushSync call inside the usePropagateFocusWhenInvalid hook
	const isExpanded = React.useMemo(() => Impulse<boolean>(() => expanded, setExpanded), [expanded]);

	usePropagateFocusWhenInvalid(link, () => setExpanded(true));

	return (
		<FormField card className="items-stretch" el={Widget} isExpanded={isExpanded} state={link}>
			<WidgetHeader title={<span className="uppercase">{name}</span>}>
				<div className="flex flex-row justify-center gap-4">
					<FormFieldReset />

					<button className="button button-sm button-tertiary button-square" onClick={onDelete} type="button">
						<IconDelete className="size-3" />
					</button>
				</div>
			</WidgetHeader>

			<WidgetBody className="space-y-4">
				<div className="grid grid-cols-[auto_max-content] grid-rows-[auto_auto_auto] gap-x-4">
					<FormField className="row-span-full grid grid-rows-subgrid" state={link.fields.url}>
						<FormLabel>URL *</FormLabel>

						{(scope, field) => (
							<Editor // configure scroll margin so the input is not hidden behind the sticky tabs
								appearance="input"
								className="scroll-mt-20"
								language={{ name: "template", variables: variables.suggestions }}
								onChange={(input) => field.setInput(input)}
								placeholder={link.fields.url.getOutput(scope)?.interpolate(variables.values) ?? ""}
								value={field.getInput(scope)}
							/>
						)}

						<FormFieldErrorMessage />
					</FormField>

					<FormField className="row-span-full grid grid-rows-subgrid" state={link.fields.targetBlank}>
						{(scope, field) => (
							<RadioButtonGroup
								className="row-start-2"
								onValueChange={(value) => field.setInput(value === "true")}
								value={String(field.getInput(scope))}
							>
								<RadioButtonGroupItem name={name} value="true">
									Open in new tab
								</RadioButtonGroupItem>

								<RadioButtonGroupItem name={name} value="false">
									Open in the same tab
								</RadioButtonGroupItem>
							</RadioButtonGroup>
						)}
					</FormField>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<FormField state={link.fields.name}>
						<FormLabel>Name</FormLabel>

						{(scope, field) => (
							<Editor
								appearance="input"
								language={{ name: "template", variables: variables.suggestions }}
								onChange={(input) => field.setInput(input)}
								placeholder={link.fields.url.getOutput(scope)?.interpolate(variables.values) ?? ""}
								value={field.getInput(scope)}
							/>
						)}
					</FormField>

					<FormField state={link.fields.tooltip}>
						<FormLabel>Tooltip</FormLabel>

						{(scope, field) => (
							<Editor
								appearance="input"
								language={{ name: "template", variables: variables.suggestions }}
								onChange={(input) => field.setInput(input)}
								value={field.getInput(scope)}
							/>
						)}
					</FormField>
				</div>
			</WidgetBody>
		</FormField>
	);
};
